import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../api/axiosConfig';

const STATUS_COLORS = {
  'Completed':   { bg: '#d1fae5', color: '#059669' },
  'In Progress': { bg: 'rgba(168,51,107,0.1)', color: '#a8336b' },
  'At Risk':     { bg: '#fee2e2', color: '#dc2626' },
  'Not Started': { bg: '#f1f5f9', color: '#64748b' },
  'On Hold':     { bg: '#fef3c7', color: '#d97706' },
  'Blocked':     { bg: '#fee2e2', color: '#dc2626' }
};

function daysOverdue(targetDate, status) {
  if (status === 'Completed') return null;
  const diff = new Date() - new Date(targetDate);
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  return days > 0 ? days : null;
}

const AccountabilityBoard = ({ missions, lockState = 'active' }) => {
  const [actionItems, setActionItems] = useState([]);
  const [loadingActions, setLoadingActions] = useState(true);

  useEffect(() => {
    // Scope to the board's SHOW switch so nobody is held accountable for an action
    // item belonging to a closed phase that isn't even listed above.
    setLoadingActions(true);
    axiosInstance.get(`/api/vision/action-items?state=${lockState}`)
      .then(r => setActionItems(r.data.data || []))
      .catch(console.error)
      .finally(() => setLoadingActions(false));
  }, [lockState]);

  // Aggregate people from: head, team, and action item responsibles
  const personMap = {}; // key: userId → { name, missions:[], head:[], actions:[] }

  const ensure = (key, name) => {
    if (!personMap[key]) personMap[key] = { user: { _id: key, fullName: name }, missions: [], headOf: [], actions: [] };
    return personMap[key];
  };

  missions.forEach(m => {
    if (m.head) {
      const key = m.head._id?.toString() || m.head.toString();
      const name = m.head.fullName || m.head.username || 'Unknown';
      ensure(key, name).headOf.push(m);
    }
    (m.team || []).forEach(person => {
      const key = person._id?.toString() || person.toString();
      const name = person.fullName || person.username || person;
      ensure(key, name).missions.push(m);
    });
  });

  actionItems.forEach(item => {
    (item.responsible || []).forEach(r => {
      if (r.userId) {
        const key = r.userId._id?.toString() || r.userId.toString();
        const name = r.userId.fullName || r.userId.username || 'Unknown';
        ensure(key, name).actions.push(item);
      } else if (r.externalName) {
        const key = `ext:${r.externalName}`;
        ensure(key, r.externalName).actions.push(item);
      }
    });
  });

  const allPersons = Object.entries(personMap)
    .map(([key, v]) => ({ key, label: v.user.fullName, isExternal: key.startsWith('ext:') }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <div>
      <div className="v26-section-header" data-tour="accountability-header">
        <span className="v26-section-title">Accountability Board</span>
        <span style={{ fontSize: 13, color: '#64748b' }}>{allPersons.length} people tracked</span>
      </div>

      {/* Mission Status Overview */}
      <div style={{ background: '#fff', borderRadius: 12, padding: 20, marginBottom: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', marginBottom: 14 }}>Mission Status Overview</div>
        <div className="v26-table-wrap">
          <table className="v26-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Mission</th>
                <th>Head</th>
                <th>Team</th>
                <th>Target</th>
                <th>Status</th>
                <th>Progress</th>
                <th>Delay</th>
              </tr>
            </thead>
            <tbody>
              {missions.map(m => {
                const overdue = daysOverdue(m.targetDate, m.status);
                const sc = STATUS_COLORS[m.status] || {};
                return (
                  <tr key={m._id}>
                    <td style={{ fontWeight: 700, color: '#94a3b8' }}>{m.order}</td>
                    <td style={{ fontWeight: 600, color: '#1e293b' }}>{m.name}</td>
                    <td style={{ fontSize: 12 }}>
                      {m.head ? (
                        <span style={{ background: '#fef3c7', color: '#92400e', borderRadius: 4, padding: '2px 6px', fontWeight: 600 }}>
                          👑 {m.head.fullName || m.head.username}
                        </span>
                      ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {(m.team || []).map(t => (
                          <span key={t._id || t} style={{ background: 'rgba(113,27,69,0.07)', color: '#711b45', borderRadius: 4, padding: '2px 6px', fontSize: 11, fontWeight: 600 }}>{t.fullName || t}</span>
                        ))}
                      </div>
                    </td>
                    <td style={{ whiteSpace: 'nowrap', fontSize: 13 }}>
                      {new Date(m.targetDate).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
                    </td>
                    <td>
                      <span style={{ ...sc, borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 600 }}>
                        {m.status}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 80, height: 6, background: '#e2e8f0', borderRadius: 99, overflow: 'hidden' }}>
                          <div style={{ width: `${m.completionPercentage}%`, height: '100%', background: m.completionPercentage >= 80 ? '#10b981' : m.completionPercentage >= 40 ? '#a8336b' : '#f59e0b', borderRadius: 99 }} />
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>{m.completionPercentage}%</span>
                      </div>
                    </td>
                    <td>
                      {overdue ? <span className="v26-acc-delay">{overdue}d overdue</span> : <span style={{ color: '#10b981', fontSize: 12 }}>On Track</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Per-person cards */}
      <div style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', marginBottom: 14 }}>Team Accountability</div>
      <div className="v26-acc-grid">
        {allPersons.map(({ key, label }) => {
          const data = personMap[key];
          const completedMissions = data.missions.filter(m => m.status === 'Completed').length;
          const atRiskMissions = data.missions.filter(m => m.status === 'At Risk' || m.autoRisk === 'High').length;
          const completedActions = data.actions.filter(a => a.status === 'Completed').length;
          const overdueActions = data.actions.filter(a => {
            if (a.status === 'Completed') return false;
            if (!a.deadline) return false;
            return new Date(a.deadline) < new Date();
          }).length;

          return (
            <div key={key} className="v26-acc-card">
              <div className="v26-acc-name">
                <span style={{ background: '#711b45', color: '#fff', borderRadius: '50%', width: 28, height: 28, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, marginRight: 8 }}>
                  {label.charAt(0).toUpperCase()}
                </span>
                {label}
              </div>

              {data.headOf.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#92400e', textTransform: 'uppercase', marginBottom: 4 }}>
                    👑 Heads ({data.headOf.length})
                  </div>
                  {data.headOf.map(m => (
                    <div key={m._id} style={{ fontSize: 12, color: '#374151', padding: '3px 0' }}>{m.name}</div>
                  ))}
                </div>
              )}

              {data.missions.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: 6 }}>
                    Missions ({data.missions.length})
                  </div>
                  {data.missions.map(m => {
                    const sc = STATUS_COLORS[m.status] || {};
                    return (
                      <div key={m._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: '1px solid #f1f5f9' }}>
                        <span style={{ fontSize: 12, color: '#374151', flex: 1 }}>{m.name}</span>
                        <span style={{ ...sc, borderRadius: 20, padding: '2px 8px', fontSize: 10, fontWeight: 600, marginLeft: 6, whiteSpace: 'nowrap' }}>{m.status}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10 }}>
                <div style={{ background: '#f8fafc', borderRadius: 8, padding: '8px 12px', textAlign: 'center' }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: '#10b981' }}>{completedMissions}/{data.missions.length}</div>
                  <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase' }}>Missions Done</div>
                </div>
                {!loadingActions && (
                  <div style={{ background: '#f8fafc', borderRadius: 8, padding: '8px 12px', textAlign: 'center' }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: overdueActions > 0 ? '#ef4444' : '#711b45' }}>
                      {completedActions}/{data.actions.length}
                    </div>
                    <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase' }}>
                      {overdueActions > 0 ? `${overdueActions} Overdue` : 'Actions Done'}
                    </div>
                  </div>
                )}
              </div>

              {atRiskMissions > 0 && (
                <div style={{ marginTop: 8, background: '#fee2e2', borderRadius: 6, padding: '5px 10px', fontSize: 12, color: '#dc2626', fontWeight: 600 }}>
                  ⚠ {atRiskMissions} mission{atRiskMissions > 1 ? 's' : ''} at risk
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AccountabilityBoard;
